import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { ProductSearchForm } from "../forms";
import { ProductPreview } from "../product/components";
import QuickAddProduct from "../product/components/QuickAddProduct";
import { SettingsContext } from "../contexts";
import { getFileUrl } from "../core/config/api.js";
import "./multi-product-search.scss";

/**
 * MultiProductSearch — multi-select picker that mirrors the SELECT PRODUCT
 * panel from the RC Listing create flow.
 *
 * - Reuses ProductSearchForm (Brand / Model / Reference filter form)
 * - Renders a grid of result cards from POST /api/products/search
 * - Each card has a select toggle ( + Add  /  ✓ Added )
 * - Selected products persist across new searches and render as chips below
 * - Confirm/Cancel buttons in the footer
 *
 * Props:
 *   - onConfirm(selected: Product[])  fired when user clicks "Add N products"
 *   - onCancel()                       fired when user clicks "Cancel"
 */

const PAGE_LIMIT = 12;

const ResultsList = ({ filters, selectedIds, onToggle }) => {
  const history = useHistory();
  const settings = useContext(SettingsContext);

  // Click image → set ?preview=<product_id>; ProductPreview (mounted at the
  // bottom of the picker) reads that param and shows the popup. Same pattern
  // the listing-create flow uses.
  const onImageClick = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const params = new URLSearchParams(history.location.search);
    params.set("preview", product.id);
    if (settings?.iframeQueryParams) {
      const iframeParams = new URLSearchParams(settings.iframeQueryParams);
      iframeParams.forEach((v, k) => { if (!params.has(k)) params.set(k, v); });
    }
    history.push(`${history.location.pathname}?${params.toString()}`);
  };

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset]   = useState(0);

  useEffect(() => {
    if (!filters) {
      setItems([]);
      setHasMore(false);
      setOffset(0);
      return;
    }
    setLoading(true);
    setOffset(0);
    axios
      .post("/api/products/search", {
        list:    "default",
        query:   filters.query || "",
        filters,
        paging:  { offset: 0, limit: PAGE_LIMIT },
      })
      .then((res) => {
        const data = res?.data;
        setItems(data?.items ?? []);
        setHasMore(data?.hasMore ?? false);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const loadMore = () => {
    const next = offset + PAGE_LIMIT;
    axios
      .post("/api/products/search", {
        list:    "default",
        query:   filters?.query || "",
        filters,
        paging:  { offset: next, limit: PAGE_LIMIT },
      })
      .then((res) => {
        const data = res?.data;
        setItems((prev) => [...prev, ...(data?.items ?? [])]);
        setHasMore(data?.hasMore ?? false);
        setOffset(next);
      });
  };

  if (!filters) {
    return (
      <div className="mps-plist-empty">
        Pick a brand or type a reference, then click Search.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mps-plist-loading">
        <span className="mps-plist-dot" />
        <span className="mps-plist-dot" />
        <span className="mps-plist-dot" />
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="mps-plist-empty">No products found</div>;
  }

  return (
    <div className="mps-plist">
      {items.map((product) => {
        const imageUrl  = product.image_id > 0 ? getFileUrl(product.image_id) : null;
        const metaParts = [product.reference_number, product.brand_name].filter(Boolean);
        const isSelected = selectedIds.has(product.id);
        return (
          <div
            key={product.id}
            className={`mps-plist-item ${isSelected ? "mps-plist-item--selected" : ""}`}
          >
            <div className="mps-plist-meta">
              {metaParts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="mps-plist-bullet">•</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="mps-plist-name">{product.model_name}</div>
            <div
              className="mps-plist-image mps-plist-image--clickable"
              role="button"
              tabIndex={0}
              onClick={(e) => onImageClick(e, product)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onImageClick(e, product); }}
              title="Open product preview"
            >
              {imageUrl ? (
                <img src={imageUrl} alt={product.model_name} />
              ) : (
                <div className="mps-plist-noimage">—</div>
              )}
            </div>
            <button
              type="button"
              className={`mps-plist-select ${isSelected ? "mps-plist-select--selected" : ""}`}
              onClick={() => onToggle(product)}
            >
              {isSelected ? "✓ Added" : "+ Add"}
            </button>
          </div>
        );
      })}
      {hasMore && (
        <button type="button" className="mps-plist-more" onClick={loadMore}>
          Load more
        </button>
      )}
    </div>
  );
};

const MultiProductSearch = ({ onConfirm, onCancel }) => {
  const [filters, setFilters]   = useState(null);
  const [selected, setSelected] = useState([]);

  const selectedIds = new Set(selected.map((p) => p.id));

  const onToggle = (product) => {
    if (!product || product.id == null) return;
    setSelected((prev) => {
      if (prev.some((p) => p.id === product.id)) {
        return prev.filter((p) => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((p) => p.id !== id));
  };

  const productLabel = (product) =>
    [product.brand_name, product.model_name, product.reference_number]
      .filter(Boolean)
      .join(" · ") || "Untitled product";

  return (
    <div className="multi-product-search">
      {/* Selected chips lead the modal, directly under the heading and close
          icon, rather than sitting in the footer above Cancel / Add — moved
          in the DOM rather than with `order: -1` so screen-reader order
          matches the visual order. */}
      {selected.length > 0 && (
        <div className="multi-product-search__chips">
          <div className="multi-product-search__chips-label">
            Selected ({selected.length})
          </div>
          <div className="multi-product-search__chips-row">
            {selected.map((product) => {
              const thumb = product.image_id > 0 ? getFileUrl(product.image_id) : null;
              return (
                <span key={product.id} className="multi-product-search__chip">
                  <span className="multi-product-search__chip-thumb">
                    {thumb ? <img src={thumb} alt="" /> : null}
                  </span>
                  <span className="multi-product-search__chip-label">
                    {productLabel(product)}
                  </span>
                  <button
                    type="button"
                    className="multi-product-search__chip-remove"
                    onClick={() => removeSelected(product.id)}
                    aria-label={`Remove ${productLabel(product)}`}
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="multi-product-search__filters">
        <ProductSearchForm
          submitAction={(f) => setFilters(f)}
          filters={filters}
        />
      </div>

      <div className="multi-product-search__results">
        <ResultsList
          filters={filters}
          selectedIds={selectedIds}
          onToggle={onToggle}
        />
        {/*
          Add-it-yourself, same as the request and listing pickers. Prominent
          until a search has actually returned something, quiet thereafter.
          Renders nothing unless RC has auto-approval on - QuickAddProduct
          decides that for itself. A product created here is added to the
          selection straight away, which is what the rep came to do.
        */}
        <QuickAddProduct
          variant={filters ? "quiet" : "prominent"}
          prefill={{
            category_id: filters?.category_id,
            brand_id: filters?.brand_id,
            reference_number: filters?.reference_number || filters?.query || "",
          }}
          onCreated={onToggle}
        />
      </div>

      <div className="multi-product-search__actions">
        <button
          type="button"
          className="multi-product-search__cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="multi-product-search__confirm-btn"
          disabled={selected.length === 0}
          onClick={() => onConfirm(selected)}
        >
          Add {selected.length} product{selected.length === 1 ? "" : "s"}
        </button>
      </div>

      {/* Mounted here so clicks on a result-card image open the preview popup
          (it reads ?preview=<id> from the URL). */}
      <ProductPreview />
    </div>
  );
};

export default MultiProductSearch;
